import type { FoundryV2DeterministicAssemblyRecord } from "../api/foundryV2Client";

function statusTone(status: "ready" | "blocked"): string {
  return status === "ready"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
    : "border-red-200 bg-red-50 text-red-700";
}

type Props = {
  deterministicAssembly: FoundryV2DeterministicAssemblyRecord | null;
  isLoading: boolean;
  error: string | null;
};

export function FoundryDeterministicAssemblyPanel({
  deterministicAssembly,
  isLoading,
  error,
}: Props): JSX.Element {
  const executionPlan = deterministicAssembly?.executionPlan;
  const summary = deterministicAssembly?.runSpecSummary;
  const differentiation = deterministicAssembly?.differentiation;
  const topStages = executionPlan?.stages.slice(0, 5) ?? [];
  const topChecks = summary?.mustPassChecks.slice(0, 5) ?? [];
  const commandCounts = summary?.commandCounts;

  return (
    <section
      className="rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#ffffff_0%,#f8fbff_100%)] p-5 shadow-[0_20px_60px_rgba(15,23,42,0.08)]"
      data-testid="foundry-deterministic-assembly-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-slate-500">
            Deterministic assembly
          </div>
          <h3 className="mt-2 text-xl font-semibold text-slate-950">
            What Foundry compiles before Codex is ever allowed to improvise
          </h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            This bundle freezes the execution graph, Jarvis run-spec, allowlists, mandatory checks, and audit posture
            from the current Foundry truth. The LLM only receives a bounded execution envelope after this contract is
            green.
          </p>
        </div>
      </div>

      {error ? (
        <div className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>
      ) : null}

      {isLoading && !deterministicAssembly ? (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
          Loading deterministic assembly bundle...
        </div>
      ) : null}

      {deterministicAssembly && summary ? (
        <>
          <div className="mt-5 grid gap-3 xl:grid-cols-[1.15fr_0.85fr_0.85fr_0.95fr]">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center gap-2">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${statusTone(
                    deterministicAssembly.launchReadiness,
                  )}`}
                >
                  {deterministicAssembly.launchReadiness}
                </span>
                <span className="text-xs text-slate-500">Compiler {deterministicAssembly.compiler.version}</span>
              </div>
              <div className="mt-3 text-sm text-slate-700">
                {summary.launchReady
                  ? "Foundry has compiled a bounded Jarvis run-spec from the current contract, with schema and lint validation already passed."
                  : "The bundle compiles, but Jarvis launch remains blocked until Foundry clears the remaining blockers."}
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                {deterministicAssembly.compiler.compiledOutsideLlm.map((item) => (
                  <span
                    key={item}
                    className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700"
                  >
                    {item}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Hashes</div>
              <div className="mt-3 space-y-2 text-[11px] text-slate-600">
                <div>
                  <span className="font-semibold text-slate-800">Assembly:</span> {deterministicAssembly.hashes.assembly}
                </div>
                <div>
                  <span className="font-semibold text-slate-800">Run-spec:</span> {deterministicAssembly.hashes.runSpec}
                </div>
                <div>
                  <span className="font-semibold text-slate-800">LEC:</span> {deterministicAssembly.hashes.lecV2}
                </div>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Run-spec</div>
              <div className="mt-2 text-sm font-semibold text-slate-900">{summary.workItemId}</div>
              <div className="mt-1 text-xs text-slate-600">{summary.title}</div>
              <div className="mt-3 space-y-1 text-xs text-slate-600">
                <div>Priority: {summary.priority}</div>
                <div>{summary.stageCount} execution stages</div>
                <div>{topChecks.length} primary must-pass checks shown</div>
              </div>
              {summary.tags.length ? (
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  {summary.tags.map((tag) => (
                    <span key={tag} className="rounded-full border border-blue-100 bg-blue-50 px-2.5 py-1 text-blue-700">
                      {tag}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Artifacts</div>
              <div className="mt-3 space-y-2 text-xs">
                <a
                  className="block font-medium text-blue-700 underline"
                  href={deterministicAssembly.artifactUrls.deterministicAssembly}
                >
                  deterministic_assembly.json
                </a>
                <a className="block font-medium text-blue-700 underline" href={deterministicAssembly.artifactUrls.jarvisRunSpec}>
                  jarvis_run_spec.json
                </a>
                <a
                  className="block font-medium text-blue-700 underline"
                  href={deterministicAssembly.artifactUrls.jarvisRunSpecValidationReport}
                >
                  jarvis_run_spec.validation_report.json
                </a>
              </div>
            </div>
          </div>

          {differentiation ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                    Differentiated assembly
                  </div>
                  <div className="mt-1 text-sm font-semibold text-slate-900">
                    {differentiation.title ?? differentiation.profileId ?? "Default profile"}
                  </div>
                  <div className="mt-1 text-xs text-slate-600">
                    {differentiation.designVariantId ?? "default-variant"} · {differentiation.selectedBrickCount} selected
                    bricks · {differentiation.uniqueFamilyCount} brick families
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 text-[11px]">
                  {(differentiation.sourceRepoIds ?? []).map((repoId) => (
                    <span key={repoId} className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-slate-700">
                      {repoId}
                    </span>
                  ))}
                </div>
              </div>
              {(differentiation.distinguishingAttributes ?? []).length ? (
                <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
                  {differentiation.distinguishingAttributes.map((attribute) => (
                    <span
                      key={attribute}
                      className="rounded-full border border-indigo-100 bg-indigo-50 px-2.5 py-1 text-indigo-700"
                    >
                      {attribute}
                    </span>
                  ))}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-5 grid gap-4 xl:grid-cols-2">
            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">Execution graph</div>
                <div className="text-xs text-slate-500">
                  {executionPlan?.stages.length ?? 0} planner stages - {summary.stageCount} Jarvis stages
                </div>
              </div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                {topStages.map((stage) => (
                  <div key={stage.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                    <div className="flex items-center justify-between gap-3">
                      <div className="font-semibold text-slate-900">{stage.label}</div>
                      <span className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                        {stage.riskTier}
                      </span>
                    </div>
                    <div className="mt-1 text-[11px] text-slate-500">
                      {stage.commands.length} commands - retry budget {stage.retryBudget}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-white/90 p-4">
              <div className="flex items-center justify-between gap-3">
                <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                  Checks and command groups
                </div>
                <div className="text-xs text-slate-500">
                  prepare {commandCounts?.prepare ?? 0} - test {commandCounts?.test ?? 0} - build {commandCounts?.build ?? 0} -
                  audit {commandCounts?.audit ?? 0}
                </div>
              </div>
              <div className="mt-3 space-y-2 text-xs text-slate-600">
                {topChecks.map((check) => (
                  <div key={check.id} className="rounded-xl border border-slate-100 bg-slate-50 px-3 py-3">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">{check.id}</div>
                    <div className="font-semibold text-slate-900">{check.description}</div>
                    <div className="mt-1 text-[11px] uppercase tracking-wide text-slate-500">{check.commandRef}</div>
                  </div>
                ))}
                {deterministicAssembly.blockers.length ? (
                  <div className="rounded-xl border border-red-100 bg-red-50 px-3 py-3 text-red-700">
                    Blockers: {deterministicAssembly.blockers.join(" - ")}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
